import { useMemo, useState } from "react";
import { Area, Bar, CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { ChartTooltip } from "@/components/common/ChartTooltip";
import { axisTick, chartTheme, LiveDot } from "@/lib/chartTheme";
import { fmtNum, toOslo } from "@/lib/format";

export const METRICS = {
  power: { key: "power", label: "Effekt", unit: "MW", color: "hsl(208 91% 36%)", colorDark: "hsl(203 88% 62%)" },
  energy: { key: "energy", label: "Produksjon", unit: "MWh", color: "hsl(208 91% 36%)", colorDark: "hsl(203 88% 62%)" },
  flow: { key: "flow", label: "Vannføring", unit: "m³/s", color: "hsl(198 82% 46%)", colorDark: "hsl(198 85% 70%)" },
  level: { key: "level", label: "Vannstand", unit: "moh", color: "hsl(172 58% 34%)", colorDark: "hsl(172 55% 52%)" },
  spot: { key: "spot", label: "Spotpris", unit: "øre/kWh", color: "hsl(30 88% 48%)", colorDark: "hsl(32 85% 62%)" },
};

const HOUR = 3600_000;
const DAY = 24 * HOUR;

/* Few, readable axis labels, taken from the data points themselves (so they sit exactly on measurements):
   hours for a day, dates along the axis for a week or a month, months for a year. The exact time comes with the pointer. */
const ticksFor = (rows, resolution, spanDays) => {
  const pts = rows.filter((r) => r.t !== null && !r.gap);
  if (!pts.length) return undefined;
  if (resolution === "hour" && spanDays <= 2) return pts.filter((r) => toOslo(new Date(r.t)).getHours() % 3 === 0).map((r) => r.t);
  if (spanDays > 120) return pts.filter((r) => toOslo(new Date(r.t)).getDate() === 1).map((r) => r.t);
  const days = resolution === "hour" ? pts.filter((r) => toOslo(new Date(r.t)).getHours() === 0) : pts;
  const stepDays = spanDays <= 10 ? 1 : spanDays <= 35 ? 5 : 14;
  return days.filter((_, i) => i % stepDays === 0).map((r) => r.t);
};

const tickFormatter = (resolution, spanDays) => (t) => {
  const d = toOslo(new Date(t));
  if (resolution === "hour" && spanDays <= 2) return format(d, "HH");
  if (spanDays > 120) return format(d, "MMM", { locale: nb });
  return spanDays <= 10 ? format(d, "EEE d.", { locale: nb }) : format(d, "d. MMM", { locale: nb });
};

const labelFormatter = (resolution) => (t) => {
  const d = toOslo(new Date(t));
  return resolution === "hour" ? format(d, "EEEE d. MMM 'kl.' HH.mm", { locale: nb }) : format(d, "EEEE d. MMM yyyy", { locale: nb });
};

/* A readable power axis: round steps (1 · 2 · 2,5 · 5 · 10 …), about four of them, and a little headroom above the
   installed capacity so its line and label have room. 2,8 MW → ticks 0 · 1 · 2 · 3 with the axis ending at 3,5. */
const niceStep = (raw) => {
  if (!raw || raw <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(raw));
  const n = raw / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
};
const powerAxis = (capacity) => {
  const top = capacity * 1.08;
  const step = niceStep(top / 4);
  const half = step / 2;
  const max = Math.ceil(top / half - 1e-9) * half;
  const ticks = [];
  for (let v = 0; v <= max + 1e-9; v += step) ticks.push(Number(v.toFixed(6)));
  const decimals = step < 1 ? 2 : Number.isInteger(step) ? 0 : 1;
  return { max, ticks, decimals };
};

/* the stop drawn where it happened: a marker at the start, the word and the time */
const StopLabel = ({ viewBox, text, color, flip }) => {
  if (!viewBox) return null;
  const x = flip ? viewBox.x - 6 : viewBox.x + 6;
  return (
    <text x={x} y={viewBox.y + 12} textAnchor={flip ? "end" : "start"} fontSize="11.5" fontWeight="600" fill={color} style={{ fontVariantNumeric: "tabular-nums" }}>
      {text}
    </text>
  );
};

/**
 * Hero chart for a plant: production (power per hour, or energy per day) as one clear line with an almost invisible
 * flat fill; optional overlays and the previous period as thin lines; stops marked directly in the chart.
 * Missing measurements break the line (they never read as zero production).
 * series: rows from /timeseries (hourly or daily); previous: aligned rows for comparison.
 */
export const ProductionChart = ({ series = [], previous = null, resolution = "hour", spanDays = 7, capacity, overlays = {}, events = [], height = 360, isDark = false }) => {
  const t = chartTheme(isDark);
  const [width, setWidth] = useState(0);
  const data = useMemo(() => {
    const step = resolution === "hour" ? HOUR : DAY;
    const rows = [];
    series.forEach((r, i) => {
      const ts = new Date(r.ts || r.date).getTime();
      const prev = rows.length ? rows[rows.length - 1] : null;
      /* a hole in the measurements becomes a hole in the line */
      if (prev && prev.t !== null && ts - prev.t > step * 1.5) rows.push({ t: prev.t + step, power: null, gap: true });
      const row = {
        t: ts,
        power: resolution === "hour" ? r.power_mw : r.energy_mwh,
        flow: resolution === "hour" ? r.flow_m3s : r.avg_flow_m3s,
        level: resolution === "hour" ? r.level_m : r.avg_level_m,
        spot: (resolution === "hour" ? r.spot_nok_mwh : r.avg_spot_nok_mwh) / 10,
        status: r.status,
      };
      if (previous && previous[i]) row.prev = resolution === "hour" ? previous[i].power_mw : previous[i].energy_mwh;
      rows.push(row);
    });
    return rows;
  }, [series, previous, resolution]);

  const primary = resolution === "hour" ? METRICS.power : METRICS.energy;
  const color = (m) => (isDark ? m.colorDark : m.color);
  const activeOverlays = Object.keys(overlays).filter((k) => overlays[k] && METRICS[k]);
  const firstOverlay = activeOverlays[0];
  const domain = data.length ? [data[0].t, data[data.length - 1].t] : ["auto", "auto"];
  const ticks = data.length ? ticksFor(data, resolution, spanDays) : undefined;
  const stopEvents = events.filter((e) => e.production_stop);
  const alarmEvents = events.filter((e) => !e.production_stop);
  const bandEnd = (e) => (e.ended_at ? new Date(e.ended_at).getTime() : domain[1]);
  const last = data.length ? data[data.length - 1] : null;
  const hasGap = data.some((d) => d.gap);
  const fmtValue = (v, name) => {
    if (v === null || v === undefined) return "Ingen måling";
    if (name === primary.label || name === "Forrige periode") return `${fmtNum(v, resolution === "hour" ? 2 : 1)} ${primary.unit}`;
    if (name === METRICS.flow.label) return `${fmtNum(v, 2)} m³/s`;
    if (name === METRICS.level.label) return `${fmtNum(v, 2)} moh`;
    if (name === METRICS.spot.label) return `${fmtNum(v, 1)} øre/kWh`;
    return fmtNum(v, 1);
  };
  const stopText = (e) => {
    const d = toOslo(new Date(e.started_at));
    const when = spanDays <= 2 ? format(d, "HH.mm") : format(d, "d. MMM 'kl.' HH.mm", { locale: nb });
    return `${e.kind === "maintenance" ? "Vedlikehold" : "Stans"} · ${when}`;
  };
  const xPct = (ts) => (domain[1] > domain[0] ? (ts - domain[0]) / (domain[1] - domain[0]) : 0);
  const axis = resolution === "hour" && capacity ? powerAxis(capacity) : null;

  if (!data.length) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        Ingen data i valgt periode. Prøv et annet intervall.
      </div>
    );
  }

  return (
    <div style={{ height }} className="relative" data-testid="kraftverk-effekt-chart" data-has-gap={hasGap ? "true" : "false"}>
      <ResponsiveContainer width="100%" height="100%" onResize={(w) => setWidth(w)}>
        <ComposedChart data={data} margin={{ top: 22, right: firstOverlay ? 4 : 12, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={t.grid} />
          <XAxis dataKey="t" type="number" scale="time" domain={domain} ticks={ticks} tickFormatter={tickFormatter(resolution, spanDays)} tick={axisTick(t)} tickLine={false} axisLine={false} tickMargin={10} />
          <YAxis yAxisId="left" tick={axisTick(t)} tickLine={false} axisLine={false} width={44} tickCount={5} ticks={axis ? axis.ticks : undefined} tickFormatter={(v) => fmtNum(v, axis ? axis.decimals : v < 10 ? 1 : 0)} domain={[0, axis ? axis.max : "auto"]} />
          {firstOverlay ? <YAxis yAxisId={firstOverlay} orientation="right" tick={axisTick(t)} tickLine={false} axisLine={false} width={48} tickCount={5} tickFormatter={(v) => fmtNum(v, firstOverlay === "level" ? 1 : firstOverlay === "spot" ? 0 : 2)} domain={firstOverlay === "level" ? ["dataMin - 0.2", "dataMax + 0.2"] : [0, "auto"]} /> : null}
          {activeOverlays.slice(1).map((k) => (
            <YAxis key={k} yAxisId={k} orientation="right" hide domain={k === "level" ? ["dataMin - 0.2", "dataMax + 0.2"] : [0, "auto"]} />
          ))}
          <Tooltip content={<ChartTooltip formatter={fmtValue} labelFormatter={labelFormatter(resolution)} />} cursor={{ stroke: t.cursor, strokeOpacity: 0.25, strokeDasharray: "3 3" }} />

          {stopEvents.map((e) => (
            <ReferenceArea key={`band-${e.id}`} yAxisId="left" x1={Math.max(new Date(e.started_at).getTime(), domain[0])} x2={Math.min(bandEnd(e), domain[1])} fill={e.kind === "maintenance" ? t.sky : t.rose} fillOpacity={0.07} strokeOpacity={0} ifOverflow="hidden" />
          ))}
          {alarmEvents.map((e) => (
            <ReferenceLine key={e.id} yAxisId="left" x={new Date(e.started_at).getTime()} stroke={e.severity === "critical" ? t.rose : e.severity === "warning" ? t.amber : t.tick} strokeDasharray="2 4" strokeOpacity={0.6} ifOverflow="hidden" />
          ))}
          {capacity && resolution === "hour" ? <ReferenceLine yAxisId="left" y={capacity} stroke={t.tick} strokeOpacity={0.4} strokeDasharray="4 5" label={{ value: `Installert ${fmtNum(capacity, 1)} MW`, position: "insideTopRight", fontSize: 11, fill: t.tick, dy: -4 }} /> : null}

          {resolution === "hour" ? (
            <Area yAxisId="left" type="monotone" dataKey="power" name={primary.label} stroke={color(primary)} strokeWidth={2} fill={color(primary)} fillOpacity={0.04} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: isDark ? "hsl(207 50% 10%)" : "#fff" }} isAnimationActive={false} connectNulls={false} />
          ) : (
            <Bar yAxisId="left" dataKey="power" name={primary.label} fill={color(primary)} fillOpacity={0.9} radius={[3, 3, 0, 0]} maxBarSize={28} isAnimationActive={false} />
          )}
          {previous ? <Line yAxisId="left" type="monotone" dataKey="prev" name="Forrige periode" stroke={t.prev} strokeOpacity={0.75} strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls={false} /> : null}
          {activeOverlays.map((k) => (
            <Line key={k} yAxisId={k} type="monotone" dataKey={k} name={METRICS[k].label} stroke={color(METRICS[k])} strokeWidth={1.5} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} connectNulls={false} />
          ))}

          {/* the stop itself, where it happened */}
          {stopEvents
            .filter((e) => new Date(e.started_at).getTime() >= domain[0])
            .map((e) => {
              const x = new Date(e.started_at).getTime();
              const flip = width ? xPct(x) > 0.72 : false;
              return <ReferenceLine key={`mark-${e.id}`} yAxisId="left" x={x} stroke={e.kind === "maintenance" ? t.sky : t.rose} strokeWidth={1.5} strokeOpacity={0.9} ifOverflow="hidden" label={<StopLabel text={stopText(e)} color={e.kind === "maintenance" ? t.sky : t.rose} flip={flip} />} />;
            })}

          {resolution === "hour" && last && last.power !== null && last.power !== undefined ? <ReferenceDot yAxisId="left" x={last.t} y={last.power} r={0} shape={(p) => <LiveDot {...p} color={t.live} />} ifOverflow="visible" /> : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
